import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import type { ComplianceScore } from "@/lib/api/types";

interface SourceRequest {
  id: string;
  identifier: string | null;
  status?: string;
}

interface PlanNeedsReviewEpic {
  id: string;
  complianceScore?: ComplianceScore | null;
  convertedFromRequests?: SourceRequest[];
}

/**
 * Whether the epic page shows the "Plan needs review" banner (ENG-290): the
 * latest audit is critical-fail and no source Epic Request has been converted
 * to this epic yet.
 *
 * The conversion guard (ENG-288) refuses to link or convert an Epic Request
 * to a critical-fail epic, so in the usual case `convertedFromRequests` is
 * empty here. A linked request whose status is anything other than
 * `converted` also counts as unconverted.
 */
export function shouldShowPlanNeedsReview(epic: PlanNeedsReviewEpic): boolean {
  if (epic.complianceScore?.tier !== "critical-fail") return false;
  const sources = epic.convertedFromRequests ?? [];
  return !sources.some((r) => r.status === "converted");
}

/** Where the banner (and the conversion error) send people: the Improve plan dialog. */
export function improvePlanHref(epicId: string): string {
  return `/epics/${epicId}?improve=1`;
}

/**
 * Banner shown on a critical-fail epic whose Epic Request is still waiting to
 * be converted. Links into the Improve plan dialog.
 */
export function PlanNeedsReviewBanner({ epic }: { epic: PlanNeedsReviewEpic }) {
  if (!shouldShowPlanNeedsReview(epic)) return null;
  const source = epic.convertedFromRequests?.[0];
  return (
    <div
      role="status"
      data-testid="plan-needs-review-banner"
      className="flex flex-col gap-2 border-b border-red-800/40 bg-red-900/[0.12] px-4 md:px-6 py-3 sm:flex-row sm:items-center"
    >
      <div className="flex items-start gap-2 flex-1 min-w-0">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-400" />
        <div className="text-sm">
          <p className="font-semibold text-red-300">Plan needs review</p>
          <p className="text-muted-foreground">
            The latest audit found critical problems with this plan, so
            {source?.identifier ? ` ${source.identifier}` : " its Epic Request"} can&apos;t be
            converted yet. Improve the plan and re-run the audit.
          </p>
        </div>
      </div>
      <Link
        to={improvePlanHref(epic.id)}
        className="text-sm font-medium text-red-300 underline underline-offset-2 hover:text-red-200 shrink-0"
      >
        Improve plan
      </Link>
    </div>
  );
}

/**
 * If `error` is the API's EPIC_CRITICAL_FAIL conversion refusal (ENG-288),
 * returns the path to the epic's Improve plan dialog (ENG-290); otherwise null.
 */
export function improvePlanPathFromError(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("data" in error)) return null;
  const body = (error as { data?: unknown }).data;
  if (!body || typeof body !== "object" || !("error" in body)) return null;
  const err = (body as { error?: { code?: unknown; details?: unknown } }).error;
  if (err?.code !== "EPIC_CRITICAL_FAIL") return null;
  const details = err.details && typeof err.details === "object" ? (err.details as Record<string, unknown>) : {};
  if (typeof details.improvePlanPath === "string" && details.improvePlanPath.startsWith("/epics/")) {
    return details.improvePlanPath;
  }
  return typeof details.epicId === "string" ? improvePlanHref(details.epicId) : null;
}
